import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { RangeReport } from '../lib/api'
import { useThemeColors } from '../lib/theme-colors'
import { cn, formatDuration } from '../lib/utils'

export function RangeReportView({
  range,
  compact = false,
}: {
  range: RangeReport
  /** Compact rendering for embedded use in narrow side panels (~380-420px).
   *  Stacks summary stats, shrinks the chart, and trims byProject cells. */
  compact?: boolean
}) {
  const totalRevenueCents = range.byProject.reduce((acc, p) => acc + (p.revenueCents ?? 0), 0)
  const colors = useThemeColors()

  const chartData = range.byProject
    .filter((p) => p.projectName)
    .map((p) => ({
      name: p.projectName ?? '—',
      hours: +(p.durationMs / 3_600_000).toFixed(2),
      revenue: p.revenueCents ?? 0,
    }))
    .slice(0, 10)

  return (
    <div className="space-y-4">
      <div className={compact ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 sm:grid-cols-3 gap-4'}>
        <Stat
          label="Gesamtzeit"
          value={formatDuration(range.totalMs)}
          accent
          sub={breakdownSub(range)}
        />
        <Stat label="Einträge" value={String(range.entries)} />
        {totalRevenueCents > 0 && (
          <Stat
            label="Umsatz (rein rechnerisch)"
            value={`${(totalRevenueCents / 100).toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} €`}
          />
        )}
      </div>

      {chartData.length > 0 && (
        <div
          className={
            compact
              ? 'rounded-xl border border-border-subtle bg-bg-elevated/40 p-2'
              : 'rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4 md:p-6 h-72'
          }
          style={compact ? { height: 180 } : undefined}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={
                compact
                  ? { top: 4, right: 8, left: 0, bottom: 0 }
                  : { top: 0, right: 16, left: 0, bottom: 0 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.gridLine} />
              <XAxis
                type="number"
                tick={{ fill: colors.textTertiary, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                unit="h"
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: colors.textSecondary, fontSize: compact ? 10 : 11 }}
                axisLine={false}
                tickLine={false}
                width={compact ? 80 : 140}
              />
              <Tooltip
                cursor={{ fill: colors.accentMuted }}
                contentStyle={{
                  backgroundColor: colors.tooltipBg,
                  border: `1px solid ${colors.tooltipBorder}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value) => [`${Number(value)}h`, 'Stunden']}
              />
              <Bar dataKey="hours" fill={colors.accent} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
        {range.byProject.map((p, i) => {
          const isLast = i === range.byProject.length - 1
          if (compact) {
            return (
              <li
                key={p.projectId ?? `null-${i}`}
                className={cn(
                  'flex items-center gap-2 px-3 py-2',
                  !isLast && 'border-b border-border-subtle',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-text-primary truncate">
                    {p.projectName ?? 'Ohne Projekt'}
                  </p>
                  {p.customerName && (
                    <p className="text-2xs text-text-tertiary truncate">{p.customerName}</p>
                  )}
                </div>
                <p className="font-mono text-xs tabular-nums text-accent shrink-0">
                  {formatDuration(p.durationMs)}
                </p>
              </li>
            )
          }
          return (
            <li
              key={p.projectId ?? `null-${i}`}
              className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-5 py-3.5 ${
                !isLast ? 'border-b border-border-subtle' : ''
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  {p.projectName ?? 'Ohne Projekt'}
                </p>
                {p.customerName && (
                  <p className="text-xs text-text-tertiary truncate">{p.customerName}</p>
                )}
              </div>
              <p className="font-mono text-xs tabular-nums text-text-tertiary">
                {p.entries} {p.entries === 1 ? 'Eintrag' : 'Einträge'}
              </p>
              {p.revenueCents != null ? (
                <p className="font-mono text-sm tabular-nums text-text-secondary">
                  {(p.revenueCents / 100).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  €
                </p>
              ) : (
                <span className="text-xs text-text-tertiary">—</span>
              )}
              <p className="font-mono text-sm tabular-nums text-accent">
                {formatDuration(p.durationMs)}
              </p>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function Stat({
  label,
  value,
  sub,
  accent,
  danger,
  warning,
}: {
  label: string
  value: string
  sub?: string | null
  accent?: boolean
  danger?: boolean
  warning?: boolean
}) {
  let valueClass = 'text-text-primary'
  if (accent && danger) valueClass = 'text-danger'
  else if (accent && warning) valueClass = 'text-warning'
  else if (accent) valueClass = 'text-accent'

  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 px-5 py-4">
      <p className="text-xs uppercase tracking-wider text-text-tertiary">{label}</p>
      <p className={`mt-1 font-mono tabular-nums text-2xl font-light ${valueClass}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] text-text-tertiary">{sub}</p>}
    </div>
  )
}

function breakdownSub(range: RangeReport): string | null {
  const parts: string[] = []
  if (range.entryMs > 0) parts.push(`Einträge ${formatDuration(range.entryMs)}`)
  if (range.meetingMs > 0) parts.push(`Meetings ${formatDuration(range.meetingMs)}`)
  if (range.absenceMs > 0) {
    const days = range.absenceDays
    const dayLabel = days === 1 ? '1 Tag' : `${days % 1 === 0 ? days : days.toFixed(1)} Tage`
    parts.push(`Abwesenheit ${formatDuration(range.absenceMs)} (${dayLabel})`)
  }
  return parts.length > 1 ? parts.join(' · ') : null
}
